import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { createRoot } from 'react-dom/client';
import axios from '../axios';
import SearchForm from '../organisms/SearchForm';
import { inputAssistDigit } from '../../ts/Convert';
import {
  search_params_init,
  project_list_staff_read_index as read_index,
  module_name,
  api_response_type,
  content_type,
  download_wait_time,
} from '../Const';
import {
  Props,
  SearchParams,
  OutputRequestDetails,
  RowSelection,
  S070N001FormOutputErrorMessage,
  PostData,
  ResponseData,
} from '../types/ProjectListStaff';
import LoadingModal from '../molecules/LoadingModal';
import Moment from 'moment';
import { handleBrowzerBack } from '../BrowzerBack';
import { parse } from 'querystring';
import ResultList from '../molecules/ResultList';
import { fileDownload } from '../FileDownload';
import { scrollPageTop } from '../Scroll';
import { jumpUrl } from '../Common';

const ProjectListStaff: React.FC = () => {
  const [projects, setProjects] = useState([]);
  const [status_options, setStatusOptions] = useState([]);
  const [result_options, setResultOptions] = useState([]);
  const [config, setConfig] = useState<Props['config']>({
    display_list_number: [],
    disabled_project_detail_status: [],
  });
  const [is_loading, setIsLoading] = useState(false);
  // 検索パラメータの初期化
  const [search_params, setSearchParams] = useState(search_params_init);
  // 最新の検索実行時のパラメータ
  const [last_search_params, setLastSearchParams] = useState(search_params_init);

  // モーダルの表示制御
  const [modal_show, setModalShow] = useState(false);
  const [modal_check_show, setModalCheckShow] = useState(false); // 案件ステータスチェック
  const [modal_message, setModalMessage] = useState('');
  const openModal = () => setModalShow(true);
  const closeModal = () => setModalShow(false);
  // 行選択の状態管理
  const [row_selection, setRowSelection] = useState<RowSelection>({});
  const [modal_output_exception, setModalOutputException] = useState(false);
  const [modal_output_exception_body, setModalOutputExceptionBody] = useState<
    JSX.Element[] | string
  >('');
  const [modal_output_confirm, setModalOutputConfirm] = useState(false);
  // Ajaxリクエスト中フラグ
  const [is_connecting, setIsConnecting] = useState(false);

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowzerBack();
    const getProject = async () => {
      try {
        //ローディング画面表示
        setIsLoading(true);
        const response = await axios.get('/api/projects/staff');
        setProjects(response.data.projects);
        setConfig(response.data.config);
        setStatusOptions(response.data.status_options);
        setResultOptions(response.data.result_options);
      } catch (error) {
        console.error(error.message);
      } finally {
        //画面トップに遷移
        scrollPageTop();
        //ローディング画面非表示
        setIsLoading(false);
      }
    };

    // 案件を検索する
    const searchProject = async (params: SearchParams) => {
      try {
        //ローディング画面表示
        setIsLoading(true);
        const response = await axios.post('/api/projects/staff/search', params);
        setProjects(response.data.projects);
        setConfig(response.data.config);
        setStatusOptions(response.data.status_options);
        setResultOptions(response.data.result_options);
      } catch (error) {
        console.error(error.message);
      } finally {
        //ローディング画面非表示
        setIsLoading(false);
      }
    };

    // 検索パラメータの初期値を設定
    const new_params = Object.assign({}, search_params);
    if (location.search.length > 0) {
      // getパラメータをパースする
      const get_queries = parse(location.search.slice(1));
      // 検索パラメータの初期化状態にマージする
      Object.assign(new_params, get_queries);
      // パラメータを更新する
      setSearchParams(new_params);
      setLastSearchParams(new_params);
    }

    // Ajaxリクエスト
    if (location.search.length > 0) {
      searchProject(new_params);
    } else {
      getProject();
    }
  }, []);

  /**
   * 検索項目を更新する(日付以外)
   *
   * @param event イベント
   * @returns void
   */
  const handleChangeSearchParams = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.currentTarget;
      setSearchParams((prev) => {
        return { ...prev, [name]: value };
      });
    },
    [setSearchParams]
  );

  /**
   * 検索項目を更新する(日付)
   *
   * @param date Date | null 入力値
   * @param key string 検索パラメータのkey
   * @returns void
   */
  const handleChangdate = useCallback(
    (value: Date | null, key: string) => {
      setSearchParams((prev) => {
        return {
          ...prev,
          [key]: value !== null ? Moment(value).format('YYYY-MM-DD') : '',
        };
      });
    },
    [setSearchParams]
  );

  /**
   * 案件番号からフォーカスアウトした際、0埋めして
   * 検索項目の値を更新する
   *
   * @param event イベント
   * @return void
   */
  const handleBlurSearchparams = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setSearchParams((prev) => {
        return name === 'reception_cd' ? { ...prev, [name]: inputAssistDigit(value) } : prev;
      });
    },
    [setSearchParams]
  );

  // 検索処理
  const handleClickSearch = useCallback(() => {
    // クリックイベント
    const searchProjects = async () => {
      try {
        //ローディング画面表示
        setIsLoading(true);
        setIsConnecting(true);
        const response = await axios.post('/api/projects/staff/search', search_params);
        const newProjects = response.data.projects; // state更新のため一旦変数に移し替える
        setProjects(newProjects);
        setLastSearchParams(search_params);
      } catch (exception) {
        console.error(exception);
        jumpUrl(exception.response.data.url);
      } finally {
        setRowSelection({});
        setIsConnecting(false);
        //ローディング画面非表示
        setIsLoading(false);
      }
    };
    searchProjects();
  }, [search_params]);

  /**
   * 案件番号のハイパーリンクをクリックする
   * @param event イベントハンドラ
   * @param id 案件id
   * @param r_cd 案件番号
   * @param status ステータス
   */
  const handleClickReceptionAnchor = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, id: string, r_cd: string, status: number) => {
      event.preventDefault();
      setIsLoading(true);
      checkStatus(event.currentTarget.href, id, r_cd, status);
    },
    [last_search_params]
  );

  /**
   * 案件ステータスチェック
   * @param href リンク先URL
   * @param id 案件ID
   * @param r_cd 案件番号
   * @param status ステータス
   */
  const checkStatus = async (href: string, id: string, r_cd: string, status: number) => {
    try {
      const response = await axios.post('/api/project/status/check', {
        reception_cd: r_cd,
        status: status,
      });

      if (response.data.result) {
        // 遷移処理
        location.href =
          href +
          // 検索パラメータを連結する
          `?first_name=${last_search_params.first_name}` +
          `&last_name=${last_search_params.last_name}` +
          `&kana_first_name=${last_search_params.kana_first_name}` +
          `&kana_last_name=${last_search_params.kana_last_name}` +
          `&status_from=${last_search_params.status_from}` +
          `&status_to=${last_search_params.status_to}` +
          `&birthday=${last_search_params.birthday}` +
          `&reception_cd=${last_search_params.reception_cd}` +
          `&reception_store=${last_search_params.reception_store}` +
          `&retailer=${last_search_params.retailer}` +
          `&execution_store=${last_search_params.execution_store}` +
          `&pre_examination_date_from=${last_search_params.pre_examination_date_from}` +
          `&pre_examination_date_to=${last_search_params.pre_examination_date_to}` +
          `&pre_examination_result=${last_search_params.pre_examination_result}` +
          `&examination_result=${last_search_params.examination_result}` +
          `&prefered_date_from=${last_search_params.prefered_date_from}` +
          `&prefered_date_to=${last_search_params.prefered_date_to}`;
      } else {
        // エラーメッセージを表示
        setIsLoading(false);
        setModalMessage(response.data.message);
        setModalCheckShow(true);
      }
    } catch (exception) {
      jumpUrl(exception.response.data.url);
      setIsLoading(false);
      setModalMessage('案件ステータスが更新されています。案件を再度選択してください。');
      setModalCheckShow(true);
    }
  };

  /**
   * 汎用エラーダイアログ（案件チェックエラー）を閉じる
   */
  const closeCheckModal = () => {
    closeModal();
    location.reload();
  };

  /**
   * 案件データCSV出力
   */
  const handleClickDownloadCsv = useCallback(async () => {
    // Ajax通信が動いている間は通信をしない
    if (is_connecting) {
      return;
    }
    //ローディング画面表示
    setIsLoading(true);
    setIsConnecting(true);

    try {
      const response = await axios.get('/api/projects/staff/download_csv', {
        params: {
          ...last_search_params,
        },
        responseType: 'blob',
      });
      fileDownload(response.data, response.headers['content-disposition'], content_type.csv);
    } catch (error) {
      jumpUrl(error.response.data.url);
      // 例外等でCSVがダウンロードできなかった場合はエラーダイアログを表示する
      setModalMessage('CSVのダウンロードができませんでした。');
      openModal();
    } finally {
      setIsConnecting(false);
      //ローディング画面非表示
      setIsLoading(false);
    }
  }, [last_search_params, is_connecting]);

  /**
   * ローンカード発行依頼書出力・ボタン押下時の処理
   */
  const handleClickOutputRequestForm = () => {
    if (!checkRowSelection()) {
      return false;
    }
    // 通信中の場合
    if (is_connecting) {
      return false;
    }

    // 確認ダイアログを開く
    setModalOutputConfirm(true);
  };

  /**
   * ローンカード発行依頼書出力・確認ダイアログのOKボタンを押下時の処理
   */
  const handleClickOutputConfirm = () => {
    try {
      // チェックボックスの情報を収集する
      if (!checkRowSelection()) {
        return false;
      }

      // 案件リストと照合してリクエストに必要なデータを生成する
      const details: OutputRequestDetails[] = [];
      Object.keys(row_selection).map((key) => {
        if (row_selection[key]) {
          // 案件リストと照合する
          projects.map((project) => {
            if (project[read_index.reception_cd] === key) {
              details.push({
                reception_id: project[read_index.reception_id], // 受付ID
                reception_cd: project[read_index.reception_cd], // 案件CD
              });
            }
          });
        }
      });

      requestOutput(details);
    } catch (exception) {
      console.error(exception);
    }
  };

  /**
   * チェックボックスの選択状態をチェックする
   * @return boolean
   */
  const checkRowSelection = () => {
    const keys = Object.keys(row_selection);
    if (keys.length === 0) {
      // stateに行の選択状態が一切記録されてない場合
      setModalOutputException(true);
      setModalOutputExceptionBody('1つ以上案件を選択してください。');
      return false;
    }
    // row_selectionの状態を収集する
    const selection: boolean[] = keys.map((key: string) => row_selection[key]);
    if (!selection.includes(true)) {
      // checkedが0個の場合
      setModalOutputException(true);
      setModalOutputExceptionBody('1つ以上案件を選択してください。');
      return false;
    }
    return true;
  };

  /**
   * チェックボックスの選択状態をチェックする
   * 選択値が1つ以下であることを確認
   *
   * @return boolean
   */
  const checkRowSelectionOnly = (): boolean => {
    // 案件のチェック状態を取得
    const checke_values = Object.values(row_selection).filter((prev) => prev === true);
    if (checke_values.length > 1) {
      setModalOutputException(true);
      setModalOutputExceptionBody('選択可能な案件は1つのみです。');
      return false;
    }
    return true;
  };

  /**
   * APIリクエスト
   * @param details
   */
  const requestOutput = async (details: OutputRequestDetails[]) => {
    try {
      setModalOutputConfirm(false);
      setIsLoading(true);
      setIsConnecting(true);
      const post_data: PostData = {
        module_name: module_name.form_output,
        detail: details,
        is_json: true,
      };
      const response = await axios.post('/api/common', post_data);

      allocationResponseData(response.data, post_data);
    } catch (exception) {
      console.error(exception);
      jumpUrl(exception.response.data.url);
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * レスポンス値を元に処理を振り分ける
   *
   * @param {ResponseData} response_data レスポンスデータ
   * @param {PostData} post_data postしたデータ
   */
  const allocationResponseData = async (response_data: ResponseData, post_data: PostData) => {
    if (response_data.result) {
      if (response_data.response_type === api_response_type.validation) {
        // エラーダイアログに表示するメッセージ文のJSX.Elementを生成する
        const error_message = () =>
          response_data.detail.map((error: S070N001FormOutputErrorMessage, index: number) => {
            if (index > 0) {
              return (
                <React.Fragment key={index}>
                  <br />
                  <br />
                  {error.message}
                </React.Fragment>
              );
            } else {
              return <React.Fragment key={index}>{error.message}</React.Fragment>;
            }
          });
        // エラーダイアログを表示する
        setIsLoading(false);
        setModalOutputExceptionBody(error_message());
        setModalOutputException(true);
      } else if (response_data.response_type === api_response_type.download) {
        try {
          const start_time = performance.now(); // 開始時間
          // ファイルダウンロード
          const download_response = await axios.post(
            '/api/common',
            { ...post_data, is_json: false },
            {
              responseType: 'blob',
            }
          );
          if (download_response.headers['content-type'] === content_type.pdf) {
            fileDownload(
              download_response.data,
              download_response.headers['content-disposition'],
              content_type.pdf
            );
            // ローディング停止を遅らせる
            const end_time = performance.now(); // 終了時間
            setTimeout(() => {
              setIsLoading(false);
            }, download_wait_time - (end_time - start_time));
          } else {
            setModalOutputExceptionBody('出力可能な案件のみ選択してください。');
            setModalOutputException(true);
            setIsLoading(false);
            console.log(download_response.data);
          }
        } catch (error) {
          console.log(error);
          setIsLoading(false);
        }
      }
    } else {
      // 例外発生
      setIsLoading(false);
      setModalOutputExceptionBody(response_data.message);
      setModalOutputException(true);
    }
  };

  /**
   * チェックボックスのcheckedを操作する
   * @param index 案件ID
   */
  const rowSelection = useCallback(
    (index: string) => {
      const new_row_selection = row_selection;
      const keys = Object.keys(row_selection);
      if (keys.length === 0) {
        new_row_selection[index] = true;
      } else {
        if (keys.includes(index)) {
          new_row_selection[index] = !new_row_selection[index];
        } else {
          new_row_selection[index] = true;
        }
      }
      setRowSelection(new_row_selection);
    },
    [row_selection]
  );

  /**
   * チェックされた案件番号を使用し、自店番号管理画面に遷移する
   */
  const handleClickSetShopCode = useCallback(() => {
    // チェック確認
    if (!checkRowSelection() || !checkRowSelectionOnly()) {
      return false;
    }

    const [reception_cd] = Object.keys(row_selection);
    const current_index = projects.findIndex(
      (project) => project[read_index.reception_cd] === reception_cd
    );
    if (current_index < 0) {
      return;
    }
    const reception_id = projects[current_index][read_index.reception_id];
    location.href = `${location.origin}${process.env.MIX_MOCK_URL}/staff/set_shop/${reception_cd}/${reception_id}`;
  }, [projects, row_selection]);

  /**
   * 検索条件をクリアする
   */
  const handleClickClear = () => {
    setSearchParams(search_params_init);
  };

  /**
   * 汎用エラーダイアログモーダルのOKクリック時の処理
   */
  const handleClickModalClose = () => {
    // CSVのエラーでダイアログを開いている
    if (modal_show) {
      closeModal();
    }

    // 案件ステータスチェックのエラーでダイアログを開いている
    if (modal_check_show) {
      closeCheckModal();
      location.reload();
    }
  };

  return (
    <div className="container">
      <div className="search-area">
        <div>
          <SearchForm
            status_options={status_options}
            result_options={result_options}
            search_params={search_params}
            handleClickSearch={handleClickSearch}
            handleChange={handleChangeSearchParams}
            handleChangeDate={handleChangdate}
            handleBlurSearchparams={handleBlurSearchparams}
            handleClickClear={handleClickClear}
            is_connecting={is_connecting}
          />
        </div>
      </div>
      <ResultList
        projects={projects}
        config={config}
        handleClick={handleClickReceptionAnchor}
        handleRowChange={rowSelection}
      />
      <div className="row">
        <Button className="btn-main-color mx-2" onClick={handleClickDownloadCsv}>
          案件データCSV出力
        </Button>
        <Button className="btn-main-color mx-2" onClick={handleClickSetShopCode}>
          店番設定
        </Button>
        {/* TODO sakuma 実装が必要か不明なため、要件が決まるまでコメントアウト */}
        {/* <MDBBtn className="btn-primary mx-2" onClick={handleClickOutputRequestForm}>
          ローンカード発行依頼書出力
        </MDBBtn>
        <span className="btn-assist-msg">
          チェックボックスで選択後、出力ボタンを押下する（14000：融資準備中かつ借入希望日当日まで印刷が可能）
        </span> */}
      </div>
      <div>
        {/* 汎用エラーダイアログ */}
        <Modal show={modal_show || modal_check_show} onHide={undefined}>
          <Modal.Body className="dialog-body">{modal_message}</Modal.Body>
          <Modal.Footer>
            <Button variant="main-color" onClick={handleClickModalClose}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
        {/* ローンカード発行依頼書出力-例外時 */}
        <Modal show={modal_output_exception} onHide={undefined}>
          <Modal.Body className="dialog-body">{modal_output_exception_body}</Modal.Body>
          <Modal.Footer>
            <Button variant="main-color" onClick={() => setModalOutputException(false)}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
        {/* ローンカード発行依頼書出力-確認ダイアログ */}
        <Modal show={modal_output_confirm} onHide={undefined}>
          <Modal.Header>確認</Modal.Header>
          <Modal.Body className="dialog-body">
            選択した案件のローンカード発行依頼書を出力します。
            <br />
            <br />
            よろしいですか？
          </Modal.Body>
          <Modal.Footer>
            <Button variant="main-color" onClick={() => handleClickOutputConfirm()}>
              はい
            </Button>
            <Button variant="main-color" onClick={() => setModalOutputConfirm(false)}>
              いいえ
            </Button>
          </Modal.Footer>
        </Modal>
        {/* ローディング画面 */}
        <LoadingModal show={is_loading} />
      </div>
    </div>
  );
};

const container = document.getElementById('project-list-staff');
if (container) {
  const root = createRoot(container);
  root.render(<ProjectListStaff />);
}
